import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { researchApi, ActiveWork } from "../../services/researchApi";
import {
  AgentActivity,
  AgentState,
  WorkflowStage,
} from "../../types/researchWorkflow";
import { queryKeys } from "./keys";

const EMPTY_ACTIVE_WORK: ActiveWork = {
  evaluation: { queued: 0, running: 0 },
  discovery: [],
};

const POLL_INTERVAL_IDLE = 10_000;
const POLL_INTERVAL_RUNNING = 5_000;

export function useTrackActivities(trackId: string | null, isRunning: boolean) {
  return useQuery({
    queryKey: queryKeys.research.trackActivities(trackId!),
    queryFn: async () => {
      if (!trackId)
        return {
          activities: [],
          agentState: "idle" as AgentState,
          activeWork: EMPTY_ACTIVE_WORK,
          lastModified: null as string | null,
        };

      const response = await researchApi.getTrackActivities(trackId, {
        limit: 50,
      });

      const transformedActivities: AgentActivity[] = response.activities.map(
        (activity) => ({
          id: activity.activity_id,
          timestamp: activity.timestamp,
          stage: activity.stage as WorkflowStage,
          action: activity.action,
          details: activity.details,
          entityId: activity.entity_id || undefined,
          userUsername: activity.user_username || undefined,
          userEmail: activity.user_email || undefined,
        }),
      );

      // Candidate-centric: "running" iff any evaluation task is queued/running
      // OR a discovery run is active. Batches no longer drive this (#888).
      const activeWork = response.active_work ?? EMPTY_ACTIVE_WORK;
      const inFlight =
        activeWork.evaluation.queued +
        activeWork.evaluation.running +
        activeWork.discovery.length;
      const agentState: AgentState = inFlight > 0 ? "running" : "idle";

      return {
        activities: transformedActivities,
        agentState,
        activeWork,
        lastModified: response.last_modified,
      };
    },
    enabled: !!trackId,
    refetchInterval: isRunning ? POLL_INTERVAL_RUNNING : POLL_INTERVAL_IDLE,
    placeholderData: keepPreviousData,
  });
}
